import * as React from "react";
import { Portal } from "components/elements";
import { AnimatePresence, motion } from "framer-motion";
import css from "./notification.module.css";

const NotificationContext = React.createContext();

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "ADD":
      return [...state, { ...payload, id: +new Date() }];
    case "REMOVE":
      return state.filter((i) => payload.id !== i.id);
    case "CLEAR":
      return [];
    default:
      return state;
  }
};

const Provider = ({ children }) => {
  const [notifications, dispatch] = React.useReducer(reducer, []);

  const add = (content) => {
    dispatch({
      payload: {
        content,
      },
      type: "ADD",
    });
  };
  const remove = (id) => {
    dispatch({
      payload: {
        id,
      },
      type: "REMOVE",
    });
  };

  React.useEffect(() => {
    if (notifications.length) {
      const needsTimeout = notifications.filter((i) => !i.interval);
      needsTimeout.forEach((i) => {
        const iTemp = { ...i };
        iTemp.interval = setTimeout(() => {
          remove(i.id);
        }, 3000);
      });
    }
  }, [notifications]);

  const contextValues = {
    add,
    notifications,
    remove,
  };

  return (
    <NotificationContext.Provider value={contextValues}>
      {typeof children === "function"
        ? React.Children.only(children(contextValues))
        : React.Children.only(children)}

      <Portal className={css.wrapper}>
        <div
          style={{
            pointerEvents: notifications.length ? null : "none",
          }}
        >
          <AnimatePresence>
            {notifications.map((i) => {
              const notificationProps = {
                className: css.item,
                key: i.id,
              };
              switch (typeof i.content) {
                case "object":
                  notificationProps.children = i.content;
                  break;
                case "function":
                  notificationProps.children = i.content({ remove });
                  break;
                case "string":
                  notificationProps.children = i.content;
                  break;
                default:
                  return null;
              }
              return <Notice {...notificationProps} />;
            })}
          </AnimatePresence>
        </div>
      </Portal>
    </NotificationContext.Provider>
  );
};

const Notice = ({ children, ...props }) => (
  <motion.div
    initial={{
      opacity: 0,
      x: -100,
    }}
    animate={{
      opacity: 1,
      x: 0,
    }}
    exit={{
      opacity: 0,
      x: 100,
    }}
    transition={{
      ease: [0.35, 0, 0.25, 1],
    }}
    role="status"
    {...props}
  >
    {children}
  </motion.div>
);

const useNotification = () => {
  const ctx = React.useContext(NotificationContext);

  if (!ctx) {
    throw Error(
      "The `useNotification` hook must be called from a descendent of the `Notification Provider`.",
    );
  }

  return {
    add: ctx.add,
    notifications: ctx.notifications,
    remove: ctx.remove,
  };
};
export default {
  Context: NotificationContext,
  Provider,
  useNotification,
};
